/**
 * useCopyCamera — la caméra du navigateur pour une page de la copie : le crochet qui branche la
 * machine (`copyCamera.ts`) sur `getUserMedia`, le viseur et la prise. Web seulement : au téléphone,
 * la copie passe par le scanner.
 *
 * Deux sources, comme l'ancien `CameraComponent.web.js` :
 *  - en live, caméra allumée : la piste de l'appel (`liveTrack`), que le tuteur continue de voir. Elle
 *    est attachée au viseur et détachée à la fin, jamais arrêtée ; la retourner la relance
 *    (`restartTrack`) ;
 *  - sinon, un flux à soi, demandé à chaque séance, avec la caméra que `preferredFacingMode` choisit
 *    (l'arrière quand l'appareil en a une, l'avant sinon). Il est arrêté à la fin de sa séance : à
 *    « Valider », à la croix, en retournant la caméra, au démontage. Un flux qui arrive pour une séance
 *    passée est arrêté aussitôt.
 *
 * La photo est l'image du viseur, à la taille de la caméra, en JPEG (une adresse de données que la
 * file de la copie envoie comme les autres pages).
 *
 * Quand la caméra ne vient pas, l'élève importe une image de ses fichiers (`importPhoto`) : elle est
 * réencodée en JPEG, comme une photo du viseur, et part par le même chemin.
 */
import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import { isHandheld, listMediaDevices, preferredFacingMode } from "../../shared/camera/cameraFacing"
import { createTelemetry } from "../../shared/telemetry"
import {
    CLOSED_CAMERA,
    cameraFailureOf,
    copyCameraReducer,
    type CameraFacing,
    type CopyCameraState,
} from "./copyCamera"

/** La piste de la caméra de l'appel, ce que l'écran en lit (`LocalVideoTrack` de LiveKit). */
export type LiveCameraTrack = {
    attach: (element: HTMLMediaElement) => HTMLMediaElement
    detach: (element: HTMLMediaElement) => unknown
    restartTrack?: (options: { facingMode: CameraFacing }) => Promise<void>
}

export type UseCopyCameraOptions = {
    open: boolean
    /** En live, caméra allumée : la piste de l'appel. Sinon `null` : un flux à soi. */
    liveTrack: LiveCameraTrack | null
    /** L'appareil, lu par l'écran (`expo-device`) : un téléphone ou une tablette. */
    deviceType: { current: unknown; phone: unknown; tablet: unknown }
}

export type CopyCamera = {
    state: CopyCameraState
    /** Le viseur : l'élément `<video>` que l'écran pose, ou `null` quand il le retire. */
    setVideo: (element: HTMLVideoElement | null) => void
    shutter: () => void
    retake: () => void
    flip: () => void
    retry: () => void
    /** Une image choisie dans les fichiers, en JPEG ; `null` : rien de choisi, ou une image illisible. */
    importPhoto: () => Promise<string | null>
    /** La dernière image importée ne s'est pas lue. */
    importFailed: boolean
    /** Retourner la caméra : un téléphone ou une tablette, comme l'ancien bouton. */
    canFlip: boolean
}

const telemetry = createTelemetry("activity.camera", { role: "student" })

/** Les mêmes contraintes que l'ancien : une préférence, jamais une exigence (pas d'écran noir sans caméra arrière). */
function constraintsFor(facing: CameraFacing | null): MediaStreamConstraints {
    return {
        audio: false,
        video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: { ideal: facing ?? "environment" },
        },
    }
}

function stopStream(stream: MediaStream | null): void {
    stream?.getTracks().forEach((track) => track.stop())
}

/** Une image dessinée à cette taille, en JPEG ; `null` sans pixels. */
function toJpeg(source: CanvasImageSource, width: number, height: number): string | null {
    if (width === 0 || height === 0) return null
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext("2d")
    if (context == null) return null
    context.drawImage(source, 0, 0, width, height)
    return canvas.toDataURL("image/jpeg", 0.92)
}

/** L'image du viseur, en JPEG ; `null` tant que la caméra n'a rien donné (le viseur encore noir). */
function grabFrame(video: HTMLVideoElement | null): string | null {
    return video == null ? null : toJpeg(video, video.videoWidth, video.videoHeight)
}

/** La plus grande taille d'une image importée : celle que la caméra demande, avec de la marge. */
const IMPORT_MAX_SIDE = 2560

/** Le sélecteur de fichiers du navigateur, images seulement ; `null` quand l'élève le ferme sans choisir. */
function pickImageFile(): Promise<File | null> {
    return new Promise((resolve) => {
        const input = document.createElement("input")
        input.type = "file"
        input.accept = "image/*"
        input.addEventListener("change", () => resolve(input.files?.[0] ?? null))
        input.addEventListener("cancel", () => resolve(null))
        input.click()
    })
}

/** L'image, réduite à `IMPORT_MAX_SIDE` et réencodée en JPEG : une page comme une photo du viseur. */
function imageToJpeg(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => {
            URL.revokeObjectURL(url)
            const scale = Math.min(1, IMPORT_MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight))
            const photo = toJpeg(image, Math.round(image.naturalWidth * scale), Math.round(image.naturalHeight * scale))
            if (photo == null) reject(new Error("the imported image has no pixels"))
            else resolve(photo)
        }
        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("the browser could not decode the imported image"))
        }
        image.src = url
    })
}

export function useCopyCamera(options: UseCopyCameraOptions): CopyCamera {
    const { open, liveTrack, deviceType } = options
    const [state, dispatch] = useReducer(copyCameraReducer, CLOSED_CAMERA)
    const [video, setVideo] = useState<HTMLVideoElement | null>(null)
    const [stream, setStream] = useState<MediaStream | null>(null)
    const [importFailed, setImportFailed] = useState(false)
    const importingRef = useRef(false)
    /** Une caméra qui échoue à chaque « Réessayer » ne fait qu'une erreur par ouverture : le reste en fils d'Ariane. */
    const failureReportedRef = useRef(false)
    const streamRef = useRef<MediaStream | null>(null)
    const liveTrackRef = useRef(liveTrack)
    liveTrackRef.current = liveTrack
    /** La caméra que tient la piste de l'appel : celle que l'appel a ouverte, l'avant. */
    const liveFacingRef = useRef<CameraFacing>("user")
    const handheld = isHandheld(deviceType.current, deviceType.phone, deviceType.tablet)

    const release = useCallback(() => {
        stopStream(streamRef.current)
        streamRef.current = null
        setStream(null)
    }, [])

    // Ouvrir : la caméra choisie d'après la liste (qui ne demande aucun droit), puis la séance.
    useEffect(() => {
        if (!open) {
            dispatch({ type: "CLOSE" })
            return
        }
        if (liveTrackRef.current != null) {
            // La piste de l'appel garde la caméra que l'appel a ouverte.
            dispatch({ type: "OPEN", facing: "user" })
            return
        }
        let cancelled = false
        listMediaDevices().then((devices: MediaDeviceInfo[]) => {
            if (!cancelled) dispatch({ type: "OPEN", facing: preferredFacingMode(devices, { handheld }) })
        })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // Chaque séance : sa source. Le flux de la séance d'avant est rendu d'abord.
    const { status, session, facing } = state
    useEffect(() => {
        if (status !== "starting") return
        telemetry.transition("closed", "starting", "open", { session, facing, live: liveTrackRef.current != null })
        const track = liveTrackRef.current
        if (track != null) {
            // Retournée : la piste de l'appel repart sur l'autre caméra (le tuteur voit la même).
            const restart = track.restartTrack != null && facing != null && facing !== liveFacingRef.current
            if (restart) liveFacingRef.current = facing
            const ready =
                restart && track.restartTrack != null ? track.restartTrack({ facingMode: facing }) : Promise.resolve()
            ready
                .then(() => dispatch({ type: "STREAM_READY", session }))
                .catch((error: unknown) => {
                    telemetry.error("live_track_restart", error)
                    dispatch({ type: "STREAM_FAILED", session, failure: cameraFailureOf(error) })
                })
            return
        }
        release()
        let current = true
        const mediaDevices = globalThis.navigator?.mediaDevices
        if (mediaDevices?.getUserMedia == null) {
            dispatch({ type: "STREAM_FAILED", session, failure: "unavailable" })
            return
        }
        mediaDevices
            .getUserMedia(constraintsFor(facing))
            .then((opened) => {
                if (!current) {
                    // La séance est passée (fermée, retournée) : ce flux n'est à personne.
                    stopStream(opened)
                    return
                }
                streamRef.current = opened
                setStream(opened)
                dispatch({ type: "STREAM_READY", session })
            })
            .catch((error: unknown) => {
                const failure = cameraFailureOf(error)
                // Un refus de l'élève, une caméra absente sont attendus : un fil d'Ariane. Le reste
                // (une caméra occupée compris) est une erreur, avec sa cause.
                if (failure === "denied" || failure === "unavailable" || failureReportedRef.current) {
                    telemetry.transition("starting", "failed", failure)
                } else {
                    failureReportedRef.current = true
                    telemetry.error("get_user_media", error, { failure })
                }
                if (current) dispatch({ type: "STREAM_FAILED", session, failure })
            })
        return () => {
            current = false
        }
    }, [status, session, facing, release])

    // Fermée : le flux à soi est arrêté. Au démontage aussi.
    useEffect(() => {
        if (status === "closed") release()
    }, [status, release])
    useEffect(() => release, [release])

    // Le viseur : la source de la séance, posée sur l'élément montré, retirée quand l'un ou l'autre part.
    const viewing = status === "ready" || status === "captured"
    useEffect(() => {
        if (video == null || !viewing) return
        const track = liveTrackRef.current
        if (track != null) {
            track.attach(video)
            return () => {
                track.detach(video)
            }
        }
        if (stream == null) return
        video.muted = true
        video.playsInline = true
        video.srcObject = stream
        video.play().catch((error: unknown) => {
            // Un viseur qui ne démarre pas reste noir : c'est ce qu'il faut savoir la prochaine fois.
            telemetry.error("viewfinder_play", error, { session })
        })
        return () => {
            video.srcObject = null
        }
    }, [video, stream, viewing, session])

    const shutter = useCallback(() => {
        const photo = grabFrame(video)
        if (photo == null) {
            telemetry.error("capture", new Error("viewfinder has no frame"), {
                session,
                ready: video?.readyState ?? null,
                live: liveTrackRef.current != null,
            })
            return
        }
        dispatch({ type: "CAPTURED", photo })
    }, [video, session])

    const importPhoto = useCallback(async (): Promise<string | null> => {
        // Une image se lit à la fois : un second choix pendant la lecture d'une grande image arriverait
        // après la fermeture de l'écran, sans activité où aller.
        if (importingRef.current) return null
        importingRef.current = true
        setImportFailed(false)
        telemetry.transition("failed", "importing", "import_opened", { failure: state.failure })
        try {
            const file = await pickImageFile()
            if (file == null) {
                telemetry.transition("importing", "failed", "import_cancelled")
                return null
            }
            try {
                const photo = await imageToJpeg(file)
                telemetry.transition("importing", "imported", "imported", { type: file.type, size: file.size })
                return photo
            } catch (error: unknown) {
                telemetry.error("import", error, { type: file.type, size: file.size })
                setImportFailed(true)
                return null
            }
        } finally {
            importingRef.current = false
        }
    }, [state.failure])

    const retry = useCallback(() => {
        setImportFailed(false)
        dispatch({ type: "RETRY" })
    }, [])

    return {
        state,
        setVideo,
        shutter,
        retake: useCallback(() => dispatch({ type: "RETAKE" }), []),
        flip: useCallback(() => dispatch({ type: "FLIP" }), []),
        retry,
        importPhoto,
        importFailed,
        canFlip: handheld,
    }
}
